/** Вкладка «Цены»: действующие на дату цены характеристик, поиск, история строки, «Исправить».
 *  «Себестоимость» = себестоимость производства с НДС (хранится) + ставка склада на «действует с» (`purchase.tsx`). */
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import { getPriceHistory, getPrices, type PriceRow } from "@/api/purchasePrices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUrlParam } from "@/lib/url-state";
import { cn } from "@/lib/utils";
import { useDebouncedText } from "../wb-regions/useDebouncedText";
import { dateRu, dateTimeRu, money, SOURCE_STYLE, todayIso } from "./format";
import { CorrectDialog } from "./PriceDialogs";
import { PurchasePriceCell } from "./purchase";
import { KEY, useRates } from "./queries";
import { Badge, CharacteristicLine, ErrorBox, Loading } from "./ui";

const COLS = 9;

export function PricesTab(): React.ReactElement {
  const [dateParam, setDate] = useUrlParam("date");
  const [q, setQ] = useUrlParam("q");
  const date = dateParam || todayIso();
  const [draft, setDraft] = useDebouncedText(q, setQ);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [correcting, setCorrecting] = useState<PriceRow | null>(null);
  const res = useQuery({ queryKey: KEY.prices(date, q), queryFn: () => getPrices(date, q) });
  const rates = useRates();

  const toggle = (id: number): void =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1 text-sm">
          <span className="block font-medium">Цены на дату</span>
          <Input
            type="date"
            className="w-44"
            value={date}
            onChange={(e) => setDate(e.target.value === todayIso() ? "" : e.target.value)}
            aria-label="Цены на дату"
          />
        </label>
        <label className="space-y-1 text-sm">
          <span className="block font-medium">Поиск</span>
          <Input
            className="w-72"
            placeholder="артикул, название, GUID"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Поиск"
          />
        </label>
        {res.data && (
          <span className="pb-2 text-sm text-muted-foreground">цен: {res.data.items.length}</span>
        )}
      </div>

      {res.isLoading && <Loading text="Загружаю цены…" />}
      {res.isError && <ErrorBox error={res.error} what="Цены не загрузились" />}
      {rates.isError && (
        <ErrorBox error={rates.error} what="Ставки склада не загрузились — себестоимость не посчитана" />
      )}
      {res.data && res.data.items.length === 0 && (
        <p className="rounded-md border px-4 py-6 text-sm text-muted-foreground" data-testid="prices-empty">
          {q ? "Ничего не найдено." : `На ${dateRu(date)} действующих цен нет.`}
        </p>
      )}
      {res.data && res.data.items.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm" data-testid="prices-table">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="w-8 px-2 py-2" />
                <th className="px-2 py-2 font-medium">Артикул</th>
                <th className="px-2 py-2 font-medium">Название</th>
                <th
                  className="px-2 py-2 text-right font-medium"
                  title="себестоимость производства с НДС + склад производства (ставка на «действует с»)"
                >
                  Себестоимость
                </th>
                <th className="px-2 py-2 text-right font-medium">Расчёт без НДС</th>
                <th className="px-2 py-2 font-medium">Источник</th>
                <th className="px-2 py-2 font-medium">Действует с</th>
                <th className="px-2 py-2 font-medium">История</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {res.data.items.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="border-t" data-testid="price-row">
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        className="rounded p-0.5 hover:bg-accent"
                        aria-label={open.has(r.id) ? "Скрыть историю" : "Показать историю"}
                        aria-expanded={open.has(r.id)}
                        onClick={() => toggle(r.id)}
                      >
                        {open.has(r.id) ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                      </button>
                    </td>
                    <td className="px-2 py-1.5 font-medium">{r.article ?? "—"}</td>
                    <td className="px-2 py-1.5">
                      {r.name ?? "—"}
                      <CharacteristicLine characteristicRef={r.characteristic_ref} name={r.characteristic_name} />
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      <PurchasePriceCell
                        gross={r.price_gross}
                        source={r.source}
                        rates={rates.data}
                        date={r.valid_from}
                        strong
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                      {r.cost_net === null ? "" : money(r.cost_net)}
                    </td>
                    <td className="px-2 py-1.5">
                      <Badge cls={SOURCE_STYLE[r.source].cls}>{SOURCE_STYLE[r.source].label}</Badge>
                    </td>
                    <td className="px-2 py-1.5 tabular-nums">{dateRu(r.valid_from)}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{r.history_count}</td>
                    <td className="px-2 py-1.5 text-right">
                      <Button size="sm" variant="outline" className="h-7" onClick={() => setCorrecting(r)}>
                        Исправить
                      </Button>
                    </td>
                  </tr>
                  {open.has(r.id) && (
                    <tr className="bg-muted/20">
                      <td colSpan={COLS} className="px-4 py-2">
                        <History nom={r.nomenclature_ref} char={r.characteristic_ref} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {correcting && <CorrectDialog row={correcting} onClose={() => setCorrecting(null)} />}
    </div>
  );
}

/** Все версии цены характеристики, новые сверху; заменённые — зачёркнуто. */
function History({ nom, char }: { nom: string; char: string }): React.ReactElement {
  const h = useQuery({ queryKey: KEY.history(nom, char), queryFn: () => getPriceHistory(nom, char) });
  const rates = useRates();
  if (h.isLoading) return <Loading text="Загружаю историю…" />;
  if (h.isError) return <ErrorBox error={h.error} what="История не загрузилась" />;
  const items = h.data?.items ?? [];
  return (
    <table className="w-full text-xs" data-testid="price-history">
      <thead className="text-left text-muted-foreground">
        <tr>
          <th className="py-1 pr-2 font-medium">Действует с</th>
          <th className="py-1 pr-2 text-right font-medium">Себестоимость</th>
          <th className="py-1 pr-2 text-right font-medium">Расчёт без НДС</th>
          <th className="py-1 pr-2 font-medium">Источник</th>
          <th className="py-1 pr-2 font-medium">Причина</th>
          <th className="py-1 font-medium">Кто, когда</th>
        </tr>
      </thead>
      <tbody>
        {items.map((v) => {
          const replaced = v.replaced_by_id !== null;
          return (
            <tr
              key={v.id}
              className={cn("border-t", replaced && "text-muted-foreground line-through")}
              data-testid="history-row"
              data-replaced={replaced}
              title={replaced ? "заменена исправлением" : undefined}
            >
              <td className="py-1 pr-2 tabular-nums">{dateRu(v.valid_from)}</td>
              <td className="py-1 pr-2 text-right tabular-nums">
                <PurchasePriceCell gross={v.price_gross} source={v.source} rates={rates.data} date={v.valid_from} />
              </td>
              <td className="py-1 pr-2 text-right tabular-nums">{v.cost_net === null ? "" : money(v.cost_net)}</td>
              <td className="py-1 pr-2">
                {SOURCE_STYLE[v.source].label}
                {v.fixation_id !== null && ` · фиксация ${v.fixation_id}`}
              </td>
              <td className="py-1 pr-2">{v.reason ?? ""}</td>
              <td className="py-1">
                {v.created_by}, {dateTimeRu(v.created_at)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
